import React, { useState, useMemo, useEffect } from 'react';
import { 
  ShoppingBag, 
  Eye, 
  Sparkles, 
  Check, 
  Star,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import { sortVariantsByWeight } from '../../utils/weightPricing';
import { getProductAltText } from '../../utils/seoHelpers';

const ProductCard = ({ product }) => {
  const { lang, t } = useLanguage();
  const { settings, mizajList, reviews, getProductRatingStats } = useStore();
  const { addToCart, setSelectedProductModal, viewProductDetail } = useCart();
  const mizajDisplay = formatMizajDisplay(product, mizajList, lang);

  const ratingStats = useMemo(() => {
    return getProductRatingStats
      ? getProductRatingStats(product.id, product.rating || 4.9, product.reviewsCount || 24)
      : { rating: product.rating || 4.9, count: product.reviewsCount || 24 };
  }, [product.id, product.rating, product.reviewsCount, reviews, getProductRatingStats]);

  // Sort variants ascending by weight (e.g. 50g -> 100g -> 250g -> 500g -> 750g -> 1kg)
  const sortedVariants = useMemo(() => {
    return sortVariantsByWeight(product.variants || []);
  }, [product.variants]);

  // Selected weight/volume variant state
  const [selectedVariant, setSelectedVariant] = useState(
    sortedVariants.length > 0 ? sortedVariants[0] : null
  );

  useEffect(() => {
    if (sortedVariants.length > 0) {
      setSelectedVariant(prev => {
        if (!prev) return sortedVariants[0];
        const match = sortedVariants.find(v => v.weight === prev.weight);
        return match || sortedVariants[0];
      });
    }
  }, [sortedVariants]);

  const [addedAnimation, setAddedAnimation] = useState(false);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product, selectedVariant, 1);
    setAddedAnimation(true);
    setTimeout(() => setAddedAnimation(false), 1200);
  };

  const currentPrice = selectedVariant ? selectedVariant.price : (sortedVariants[0]?.price || 0);

  return (
    <div
      onClick={() => {
        if (viewProductDetail) {
          viewProductDetail(product);
        } else {
          setSelectedProductModal(product);
        }
      }}
      className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-pansar-300 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer"
    >
      {/* Top Image & Badges */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
        <img
          src={product.image}
          alt={getProductAltText(product, 0, lang)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        {/* Stock status badge */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
          {product.inStock ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold font-urdu shadow-sm">
              <CheckCircle2 className="w-3 h-3" />
              <span>{t('inStock')}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600 text-white text-[11px] font-bold font-urdu shadow-sm">
              <AlertCircle className="w-3 h-3" />
              <span>{t('outOfStock')}</span>
            </span>
          )}

          {product.featured && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-slate-950 text-[10px] font-extrabold font-urdu shadow-sm">
              <Sparkles className="w-3 h-3" />
              <span>{t('bestSeller')}</span>
            </span>
          )}
        </div>

        {/* Mizaj Badge (پہلے عمومی گروپ پھر کامہ لگا کر درجہ بندی) */}
        {mizajDisplay && (
          <div className="absolute bottom-3 right-3">
            <span className="px-2.5 py-1 rounded-lg bg-black/75 backdrop-blur-md text-amber-300 text-[11px] font-semibold font-urdu border border-white/10">
              {mizajDisplay}
            </span>
          </div>
        )}

        {/* Quick Details Hover Preview Icon */}
        <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProductModal(product);
            }}
            className="p-2 rounded-full bg-white/90 hover:bg-white text-gray-700 shadow-md transition"
            title={t('viewDetails')}
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Product Content */}
      <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
        <div>
          {/* Rating */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="flex text-amber-400">
              <Star className="w-3.5 h-3.5 fill-current" />
            </div>
            <span className="text-xs font-bold text-gray-700 font-sans">
              {ratingStats.rating}
            </span>
            <span className="text-[11px] text-gray-400 font-sans">
              ({ratingStats.count})
            </span>
          </div>

          {/* Product Titles */}
          <h3 className="font-bold text-base sm:text-lg text-gray-900 group-hover:text-pansar-800 transition-colors font-subheading line-clamp-1">
            {lang === 'ur' ? product.nameUrdu : (product.nameEn || product.nameUrdu)}
          </h3>
          <p className="text-xs text-gray-400 font-sans line-clamp-1 mb-2">
            {lang === 'ur' ? product.nameEn : product.nameUrdu}
          </p>

          {/* Tibb-e-Pakistani Benefits / Properties Snippet */}
          <p className="text-xs text-gray-600 font-urdu line-clamp-2 leading-relaxed mb-3">
            {lang === 'ur' ? product.benefitsUrdu : (product.benefitsEn || product.benefitsUrdu)}
          </p>

          {/* Weight / Packaging Variant Selector */}
          {sortedVariants && sortedVariants.length > 0 && (
            <div className="mb-4" onClick={(e) => e.stopPropagation()}>
              <label className="block text-[11px] font-semibold text-gray-500 font-urdu mb-1.5">
                {t('selectWeight')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {sortedVariants.map((v, i) => {
                  const isSelected = selectedVariant?.weight === v.weight;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedVariant(v)}
                      className={`px-2 py-1 text-xs rounded-lg border font-urdu transition-all ${
                        isSelected
                          ? 'border-pansar-700 bg-pansar-700 text-white font-bold shadow-sm'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {lang === 'ur' ? v.weight : (v.weightEn || v.weight)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Pricing & Call to Action Buttons */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <span className="text-xs text-gray-400 font-urdu">{t('price')}</span>
              <div className="text-lg sm:text-xl font-extrabold text-pansar-900 font-sans">
                {formatPrice(currentPrice, lang)}
              </div>
            </div>
            {selectedVariant && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-urdu">
                {lang === 'ur' ? selectedVariant.weight : (selectedVariant.weightEn || selectedVariant.weight)}
              </span>
            )}
          </div>

          {/* Action Button: Add to Cart */}
          <div onClick={(e) => e.stopPropagation()}>
            <button
              disabled={!product.inStock}
              onClick={handleAddToCart}
              className={`w-full py-3 px-4 rounded-2xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition ${
                !product.inStock
                  ? 'bg-rose-50 text-rose-700 border border-rose-200 cursor-not-allowed opacity-90'
                  : addedAnimation
                  ? 'bg-emerald-600 text-white shadow-md cursor-pointer'
                  : 'bg-pansar-800 hover:bg-pansar-900 text-white shadow-sm hover:shadow-md cursor-pointer'
              }`}
            >
              {addedAnimation ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>{lang === 'ur' ? 'کارٹ میں شامل ہو گیا ✓' : 'Added to Cart ✓'}</span>
                </>
              ) : !product.inStock ? (
                <>
                  <AlertCircle className="w-4 h-4 text-rose-500" />
                  <span>{lang === 'ur' ? 'اسٹاک ختم ہے (Out of Stock)' : 'Out of Stock'}</span>
                </>
              ) : (
                <>
                  <ShoppingBag className="w-4 h-4 text-amber-300" />
                  <span>{t('addToCart')}</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductCard;
